import { roundMoney } from './finalInvoiceTotals';
import { formatEuro } from './offerDocumentUtils';
import { formatGermanNumber } from '../../../utils/parseGermanNumber';
import type { PartialInvoiceMode } from './invoiceLedgerSnapshot';

export const ABSCHLAG_PERCENT_PRESETS = [10, 25, 50, 75, 100] as const;
export const ABSCHLAG_PERCENT_MAX_FRACTION_DIGITS = 5;

export function isAbschlagPresetPercent(percent: number): boolean {
  return (ABSCHLAG_PERCENT_PRESETS as readonly number[]).includes(percent);
}

export function roundAbschlagPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const scaled = Math.round(Math.max(0, Math.min(100, value)) * 10 ** ABSCHLAG_PERCENT_MAX_FRACTION_DIGITS);
  return scaled / 10 ** ABSCHLAG_PERCENT_MAX_FRACTION_DIGITS;
}

export function parsePartialInvoiceMode(raw: unknown): PartialInvoiceMode | undefined {
  if (raw === 'percent' || raw === 'amount' || raw === 'position') return raw;
  return undefined;
}

export function isLumpSumPartialInvoiceMode(mode: PartialInvoiceMode | null | undefined): boolean {
  return mode !== 'position';
}

/**
 * Kumuliert = Prozent/Betrag ist der Leistungsstand insgesamt, bisherige Abschläge werden
 * abgezogen. Ohne explizite Wahl: Prozent kumuliert, Betrag nicht (Verhalten vor dem Schalter).
 */
export function isCumulativePartialInvoice(
  mode: PartialInvoiceMode | null | undefined,
  cumulative: boolean | null | undefined,
): boolean {
  if (mode === 'position') return false;
  return cumulative ?? mode !== 'amount';
}

export function remainingPartialInvoiceNet(offerNet: number, priorPartialNet: number): number {
  return roundMoney(Math.max(0, Number(offerNet) || 0) - Math.max(0, Number(priorPartialNet) || 0));
}

export type PartialInvoiceBaseNetInput = {
  mode: PartialInvoiceMode;
  offerNet: number;
  priorPartialNet: number;
  abschlagPercent: number;
  partialInvoiceAmount: number;
  positionNet: number;
  /**
   * Betrag-Modus: auf „Angebot − bisherige Abschläge“ begrenzen. Default true.
   * false, wenn es kein Angebot gibt — dann gilt der eingegebene Betrag unverändert.
   */
  capAtRemaining?: boolean;
};

export type PartialInvoiceBaseNetResult = {
  baseNet: number;
  remainingNet: number;
  clampedAmount: number;
  exceededRemaining: boolean;
};

/**
 * Base net for the current Abschlagsrechnung, before VAT.
 *
 * Percent mode is `offerNet × percent`, amount mode the entered net (optionally capped
 * at the remaining offer net). Whether prior Abschläge are subtracted afterwards
 * (`computeFinalInvoiceTotals`) is decided by `isCumulativePartialInvoice`.
 * Position mode uses the billed-row net.
 *
 * Skonto never enters this calculation: it is a payment condition, not a
 * reduction of stored invoice-chain amounts.
 */
export function computePartialInvoiceBaseNet(input: PartialInvoiceBaseNetInput): PartialInvoiceBaseNetResult {
  const remainingNet = remainingPartialInvoiceNet(input.offerNet, input.priorPartialNet);
  if (input.mode === 'position') {
    return {
      baseNet: roundMoney(Math.max(0, input.positionNet)),
      remainingNet,
      clampedAmount: 0,
      exceededRemaining: false,
    };
  }
  if (input.mode === 'amount') {
    const requested = roundMoney(Math.max(0, Number(input.partialInvoiceAmount) || 0));
    if (input.capAtRemaining === false) {
      return { baseNet: requested, remainingNet, clampedAmount: requested, exceededRemaining: false };
    }
    const clampedAmount = roundMoney(Math.min(requested, remainingNet));
    return {
      baseNet: clampedAmount,
      remainingNet,
      clampedAmount,
      exceededRemaining: requested > remainingNet + 0.00001,
    };
  }
  const percent = roundAbschlagPercent(input.abschlagPercent) / 100;
  return {
    baseNet: roundMoney((Number(input.offerNet) || 0) * percent),
    remainingNet,
    clampedAmount: 0,
    exceededRemaining: false,
  };
}

/** Brutto zu einem Netto, gerechnet wie die Summen der Rechnung (USt je Beleg gerundet). */
export function grossFromNetAmount(net: number, vatRatePercent: number): number {
  const n = roundMoney(Math.max(0, Number(net) || 0));
  return roundMoney(n + roundMoney(n * (vatRatePercent / 100)));
}

/**
 * Netto zu einem eingegebenen Brutto. Wählt unter den gerundeten Kandidaten den, dessen
 * Rechnungs-Brutto (Netto + gerundete USt) genau den eingegebenen Betrag ergibt.
 */
export function netFromGrossAmount(gross: number, vatRatePercent: number): number {
  const g = roundMoney(Math.max(0, Number(gross) || 0));
  const approx = roundMoney(g / (1 + vatRatePercent / 100));
  for (const candidate of [approx, roundMoney(approx - 0.01), roundMoney(approx + 0.01)]) {
    if (candidate >= 0 && grossFromNetAmount(candidate, vatRatePercent) === g) return candidate;
  }
  return approx;
}

export function formatAbschlagPercentLabel(percent: number): string {
  return formatGermanNumber(roundAbschlagPercent(percent), {
    minimumFractionDigits: 0,
    maximumFractionDigits: ABSCHLAG_PERCENT_MAX_FRACTION_DIGITS,
  });
}

/** Default-Beschreibung der Abschlagszeile; Angebot und Leistungsort nur, wenn vorhanden. */
export function formatPartialInvoiceDescriptionFallback(
  offerReferenceId: string | null | undefined,
  leistungsort: string | null | undefined,
): string {
  const offerRef = String(offerReferenceId ?? '').trim();
  const ort = String(leistungsort ?? '').trim();
  const head = offerRef ? `Abschlag gemäß Angebot ${offerRef}` : 'Abschlag';
  return ort ? `${head} - ${ort}` : head;
}

export function formatPartialInvoiceLineName(opts: {
  mode: PartialInvoiceMode;
  percent: number;
  netAmount: number;
  offerReferenceId?: string | null;
}): string {
  // Ohne Angebotsnummer (z. B. Abschlag ohne verschicktes Angebot) kein „auf Angebot —“.
  const offerRef = String(opts.offerReferenceId ?? '').trim();
  const offerSuffix = offerRef ? ` auf Angebot ${offerRef}` : '';
  // Nach Betrag steht der Betrag schon in Preis/Gesamt — im Titel wäre er doppelt.
  if (opts.mode === 'amount') {
    return `Abschlagsrechnung${offerSuffix}`;
  }
  return `Abschlagsrechnung – ${formatAbschlagPercentLabel(opts.percent)} %${offerSuffix}`;
}
